import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { NotificationType, NotificationPriority } from '../../../types/user';
import { NotificationService } from '../../../services/notificationService';
import { collection, query, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface TestNotificationFormProps {
  onSent?: () => void;
}

interface UserOption {
  uid: string;
  displayName: string;
  email: string;
}

/**
 * TestNotificationForm - Create and send test notifications
 * Allows admins to test notification creation with custom content
 */
const TestNotificationForm: React.FC<TestNotificationFormProps> = ({ onSent }) => {
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [type, setType] = useState<NotificationType>('admin_broadcast');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<NotificationPriority>(NotificationPriority.NORMAL);
  const [actionLabel, setActionLabel] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Load users when autocomplete is focused
  const handleLoadUsers = async () => {
    if (userOptions.length > 0) return; // Already loaded

    setLoadingUsers(true);
    try {
      const q = query(collection(db, 'users'), limit(50));
      const snapshot = await getDocs(q);
      const users: UserOption[] = snapshot.docs.map(doc => ({
        uid: doc.id,
        displayName: doc.data().displayName || 'Unknown',
        email: doc.data().email || '',
      }));
      setUserOptions(users);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedUser) {
      setResult({ success: false, message: 'Please select a recipient' });
      return;
    }

    if (!title.trim() || !message.trim()) {
      setResult({ success: false, message: 'Please enter a title and message' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      await NotificationService.createNotification({
        userId: selectedUser.uid,
        type,
        title,
        message,
        priority,
        actionLabel: actionLabel.trim() || undefined,
        actionUrl: actionUrl.trim() || undefined,
        data: {
          test: true,
          sentFrom: 'admin-testing-panel',
        },
      });

      setResult({
        success: true,
        message: `Test notification sent successfully to ${selectedUser.displayName}!`,
      });

      // Reset form
      setTitle('');
      setMessage('');
      setActionLabel('');
      setActionUrl('');

      if (onSent) {
        onSent();
      }
    } catch (error: any) {
      console.error('Error sending test notification:', error);
      setResult({
        success: false,
        message: `Failed to send: ${error.message}`,
      });
    } finally {
      setSending(false);
    }
  };

  const notificationTypes: NotificationType[] = [
    'admin_broadcast',
    'system_announcement',
    'feature_announcement',
    'maintenance_scheduled',
    'welcome',
    'custom',
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Send Test Notification
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Create and send a test notification to verify functionality
      </Typography>

      <Grid container spacing={3}>
        {/* Recipient Selection */}
        <Grid item xs={12}>
          <Autocomplete
            options={userOptions}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            onFocus={handleLoadUsers}
            loading={loadingUsers}
            getOptionLabel={(option) => `${option.displayName} (${option.email})`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipient User"
                required
                helperText="Select a user to receive the test notification"
              />
            )}
            disabled={sending}
          />
        </Grid>

        {/* Notification Type */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>Notification Type</FormLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as NotificationType)}
              disabled={sending}
            >
              {notificationTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t.replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1 }}>Priority</FormLabel>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value as NotificationPriority)}
              disabled={sending}
            >
              <MenuItem value={NotificationPriority.LOW}>Low</MenuItem>
              <MenuItem value={NotificationPriority.NORMAL}>Normal</MenuItem>
              <MenuItem value={NotificationPriority.HIGH}>High</MenuItem>
              <MenuItem value={NotificationPriority.CRITICAL}>Critical</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={sending}
            required
            inputProps={{ maxLength: 100 }}
            helperText={`${title.length}/100 characters`}
          />
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
            required
            inputProps={{ maxLength: 500 }}
            helperText={`${message.length}/500 characters`}
          />
        </Grid>

        {/* Action Button (Optional) */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Action Button Label (optional)"
            value={actionLabel}
            onChange={(e) => setActionLabel(e.target.value)}
            disabled={sending}
            placeholder="e.g., View Details"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Action Button URL (optional)"
            value={actionUrl}
            onChange={(e) => setActionUrl(e.target.value)}
            disabled={sending}
            placeholder="/path/to/page"
          />
        </Grid>

        {/* Result Message */}
        {result && (
          <Grid item xs={12}>
            <Alert severity={result.success ? 'success' : 'error'}>
              {result.message}
            </Alert>
          </Grid>
        )}

        {/* Send Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSendTest}
            disabled={sending || !selectedUser}
          >
            {sending ? 'Sending...' : 'Send Test Notification'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TestNotificationForm;
